import { useCallback, useRef } from 'react'
import type { Subscription } from 'zen-observable-ts'
import { loginMemberSeBankId } from '@/services/authApi/login'
import { exchangeAuthorizationCode } from '@/services/authApi/oauth'
import { saveAuthTokensClientSide } from '@/services/authApi/persist'
import { useShopSession } from '@/services/shopSession/ShopSessionContext'
import { useRoutingLocale } from '@/utils/l10n/useRoutingLocale'
import { datadogRum } from '@datadog/browser-rum'
import { useLoginMemberSsnLazyQuery, useShopSessionCustomerUpdateMutation } from '../graphql/generated'
import type { BankIdLoginOptions } from './bankId.types'
import { apiStatusToBankIdState, bankIdLogger } from './bankId.utils'
import type { BankIdDispatch } from './bankIdReducer'
import { getIsBankIdSandboxSelected } from './bankIdSandbox'

type HookOptions = {
  dispatch: BankIdDispatch
}

export const useBankIdLogin = ({ dispatch }: HookOptions) => {
  const { startLogin, cancelLogin } = useBankIdLoginApi({ dispatch })

  const startLoginWithCallbacks = useCallback(
    (options: BankIdLoginOptions) => {
      datadogRum.addAction('bankIdLogin start')
      dispatch({ type: 'startLogin', ssn: options.ssn })

      startLogin({
        ...options,
        async onSuccess() {
          datadogRum.addAction('bankIdLogin complete')
          // Wrap into Promise.resolve to allow both sync and async callbacks
          await Promise.resolve(options.onSuccess?.())
          dispatch({ type: 'success' })
        },
      })
    },
    [startLogin, dispatch],
  )

  const cancelLoginWithCallbacks = useCallback(() => {
    cancelLogin()
    datadogRum.addAction('bankIdLogin cancel')
    dispatch({ type: 'cancel' })
  }, [cancelLogin, dispatch])

  return {
    startLogin: startLoginWithCallbacks,
    cancelLogin: cancelLoginWithCallbacks,
  }
}

const useBankIdLoginApi = ({ dispatch }: HookOptions) => {
  const locale = useRoutingLocale()
  const { shopSession, reset: resetShopSession } = useShopSession()
  const [updateCustomer] = useShopSessionCustomerUpdateMutation()
  const [getCurrentMemberSsn] = useLoginMemberSsnLazyQuery()
  const subscriptionRef = useRef<Subscription | null>(null)

  const startLogin = useCallback(
    ({ ssn, onSuccess }: BankIdLoginOptions) => {
      bankIdLogger.info('Starting BankId login')
      // Future ideas
      // - try Observable.from().forEach to await final result and Promise.finally to clean up ref
      subscriptionRef.current = loginMemberSeBankId(ssn, locale, getIsBankIdSandboxSelected()).subscribe({
        async next(statusResponse) {
          dispatch({
            type: 'operationStateChange',
            nextOperationState: apiStatusToBankIdState(statusResponse.status),
          })

          const { liveQrCodeData, autoStartToken, bankidAppOpened } = statusResponse.seBankIdProperties
          dispatch({
            type: 'propertiesUpdate',
            qrCodeData: liveQrCodeData,
            autoStartToken,
            bankidAppOpened,
          })

          if (statusResponse.status === 'COMPLETED' && statusResponse.authorizationCode) {
            const { accessToken, refreshToken } = await exchangeAuthorizationCode(statusResponse.authorizationCode)
            saveAuthTokensClientSide({ accessToken, refreshToken })
            bankIdLogger.info('Login completed | Got access token')

            const { data } = await getCurrentMemberSsn()
            const currentMemberSsn = data?.currentMember.ssn
            const shopSessionSsn = shopSession?.customer?.ssn
            const isIdentifiedSession = shopSessionSsn != null
            if (isIdentifiedSession) {
              // Identified --> Verified customer session transition.
              // We reset shop session if verified customer SSN differs from shopSession SSN otherwise e-commerce app
              // would be in a weird state where potential quotes/cart items would be associated with someone else
              // other than the verified customer. If SSN matches, nothing needs to be done.
              const shouldResetShopSession = currentMemberSsn !== shopSessionSsn
              if (shouldResetShopSession) {
                resetShopSession()
                bankIdLogger.info(
                  'Login completed | Reset shop session because verified customer SSN differs from shopSession SSN',
                )
              }
            } else {
              // It shouldn't happen the case where shopSession is not available at this point.
              // Just being safe
              if (!shopSession) {
                bankIdLogger.warn('Login completed | No shopSession available, skipping shop session sync')
              } else {
                // Anonymous --> Verified customer session transition.
                // We update existing shop session by attaching customer SSN so we don't need to ask about it
                // on subsequent purchase flows.
                await updateCustomer({
                  variables: {
                    input: {
                      shopSessionId: shopSession.id,
                      ssn: currentMemberSsn,
                    },
                  },
                })
              }
            }

            onSuccess?.()
          }
        },
        complete() {
          subscriptionRef.current = null
        },
        error(error?: string) {
          subscriptionRef.current = null
          dispatch({ type: 'error', error })
        },
      })
    },
    [dispatch, locale, shopSession, getCurrentMemberSsn, resetShopSession, updateCustomer],
  )
  const cancelLogin = useCallback(() => {
    subscriptionRef.current?.unsubscribe()
  }, [])
  return { startLogin, cancelLogin }
}
